import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function SalaryConfigDialog({ open, onClose, employee, token, onSuccess }) {
    const [formData, setFormData] = useState({
        base_hourly_rate: 0,
        percent_total_revenue: 0,
        percent_souvenirs: 0,
        percent_cafe: 0,
        min_guaranteed: 0,
        enable_revenue_percent: false,
        enable_souvenirs_percent: false,
        enable_cafe_percent: false,
        enable_min_guaranteed: false
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (employee && open) {
            const config = employee.salary_config || {};
            setFormData({
                base_hourly_rate: config.base_hourly_rate || config.base_salary || employee.hourly_rate || 0,
                percent_total_revenue: config.percent_total_revenue || 0,
                percent_souvenirs: config.percent_souvenirs || 0,
                percent_cafe: config.percent_cafe || 0,
                min_guaranteed: config.min_guaranteed || 0,
                enable_revenue_percent: (config.percent_total_revenue || 0) > 0,
                enable_souvenirs_percent: (config.percent_souvenirs || 0) > 0,
                enable_cafe_percent: (config.percent_cafe || 0) > 0,
                enable_min_guaranteed: (config.min_guaranteed || 0) > 0
            });
            setError('');
            setSuccess('');
        }
    }, [employee, open]);

    const handleSubmit = async () => {
        try {
            const baseHourlyRate = parseFloat(formData.base_hourly_rate) || 0;
            const config = {
                base_hourly_rate: baseHourlyRate,
                base_salary: baseHourlyRate,
                percent_total_revenue: formData.enable_revenue_percent ? (parseFloat(formData.percent_total_revenue) || 0) : 0,
                percent_souvenirs: formData.enable_souvenirs_percent ? (parseFloat(formData.percent_souvenirs) || 0) : 0,
                percent_cafe: formData.enable_cafe_percent ? (parseFloat(formData.percent_cafe) || 0) : 0,
                min_guaranteed: formData.enable_min_guaranteed ? (parseFloat(formData.min_guaranteed) || 0) : 0
            };

            // Валидация
            if (config.percent_total_revenue < 0 || config.percent_total_revenue > 100) {
                setError('Процент от общей выручки должен быть от 0 до 100');
                return;
            }

            if (config.percent_souvenirs < 0 || config.percent_souvenirs > 100) {
                setError('Процент от сувениров должен быть от 0 до 100');
                return;
            }

            if (config.percent_cafe < 0 || config.percent_cafe > 100) {
                setError('Процент от кафе должен быть от 0 до 100');
                return;
            }

            const response = await fetch(`${API_URL}/api/employees/${employee.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    salary_config: config
                })
            });

            const data = await response.json();

            if (!data.success) {
                setError(data.error);
                return;
            }

            setSuccess('Настройки зарплаты обновлены');
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1000);

        } catch (error) {
            setError('Ошибка при сохранении');
        }
    };

    const estimateMonthlyPay = () => {
        const avgHours = 160;
        const baseRate = parseFloat(formData.base_hourly_rate) || 0;
        let basePay = baseRate * avgHours;

        // Примерные значения для демонстрации
        const avgRevenue = 100000;
        const avgSouvenirs = 20000;
        const avgCafe = 15000;

        let revenuePay = 0;
        if (formData.enable_revenue_percent) {
            revenuePay += avgRevenue * (parseFloat(formData.percent_total_revenue) || 0) / 100;
        }
        if (formData.enable_souvenirs_percent) {
            revenuePay += avgSouvenirs * (parseFloat(formData.percent_souvenirs) || 0) / 100;
        }
        if (formData.enable_cafe_percent) {
            revenuePay += avgCafe * (parseFloat(formData.percent_cafe) || 0) / 100;
        }

        let total = basePay + revenuePay;

        const minGuaranteed = formData.enable_min_guaranteed ? (parseFloat(formData.min_guaranteed) || 0) : 0;
        if (minGuaranteed > 0 && total < minGuaranteed) {
            total = minGuaranteed;
        }

        return {
            base: basePay,
            revenue: revenuePay,
            total: total,
            guaranteed: minGuaranteed
        };
    };

    const estimate = estimateMonthlyPay();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Настройки зарплаты: {employee?.full_name}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Должность: <strong>{employee?.position}</strong>
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Базовая ставка */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Базовая ставка
                </Typography>
                <TextField
                    fullWidth
                    label="Базовая ставка в час"
                    type="number"
                    value={formData.base_hourly_rate}
                    onChange={(e) => setFormData({ ...formData, base_hourly_rate: e.target.value })}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">₽/ч</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 10 }}
                    helperText="Часовая ставка сотрудника. В расчете умножается на отработанные часы."
                />

                <Divider sx={{ my: 3 }} />

                {/* Проценты от выручки */}
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Проценты от выручки
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.enable_revenue_percent}
                                onChange={(e) => setFormData({ ...formData, enable_revenue_percent: e.target.checked })}
                            />
                        }
                        label="Процент от общей выручки"
                    />
                    {formData.enable_revenue_percent && (
                        <TextField
                            fullWidth
                            label="Процент от общей выручки"
                            type="number"
                            value={formData.percent_total_revenue}
                            onChange={(e) => setFormData({ ...formData, percent_total_revenue: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            helperText="Процент от всей выручки посещений"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.enable_souvenirs_percent}
                                onChange={(e) => setFormData({ ...formData, enable_souvenirs_percent: e.target.checked })}
                            />
                        }
                        label="Процент от сувениров"
                    />
                    {formData.enable_souvenirs_percent && (
                        <TextField
                            fullWidth
                            label="Процент от сувениров"
                            type="number"
                            value={formData.percent_souvenirs}
                            onChange={(e) => setFormData({ ...formData, percent_souvenirs: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            helperText="Процент от продажи сувениров"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.enable_cafe_percent}
                                onChange={(e) => setFormData({ ...formData, enable_cafe_percent: e.target.checked })}
                            />
                        }
                        label="Процент от кафе"
                    />
                    {formData.enable_cafe_percent && (
                        <TextField
                            fullWidth
                            label="Процент от кафе"
                            type="number"
                            value={formData.percent_cafe}
                            onChange={(e) => setFormData({ ...formData, percent_cafe: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            helperText="Процент от заказов в кафе"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Минимальная гарантия */}
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.enable_min_guaranteed}
                                onChange={(e) => setFormData({ ...formData, enable_min_guaranteed: e.target.checked })}
                            />
                        }
                        label="Минимальная гарантированная сумма"
                    />
                    {formData.enable_min_guaranteed && (
                        <TextField
                            fullWidth
                            label="Минимальная гарантия"
                            type="number"
                            value={formData.min_guaranteed}
                            onChange={(e) => setFormData({ ...formData, min_guaranteed: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">₽</InputAdornment>,
                            }}
                            inputProps={{ min: 0, step: 1000 }}
                            helperText="Минимальная сумма, если расчет дал меньше"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Примерный расчет */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Примерный расчет (при средней выручке):
                    </Typography>

                    <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={8}>
                            <Typography variant="body2">Базовая ставка:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="medium">
                                {estimate.base.toLocaleString()} ₽
                            </Typography>
                        </Grid>

                        {estimate.revenue > 0 && (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2">Проценты от выручки:</Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" fontWeight="medium">
                                        +{estimate.revenue.toLocaleString()} ₽
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={8}>
                            <Typography variant="body1" fontWeight="bold">Итого:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="body1" fontWeight="bold" color="primary">
                                {estimate.total.toLocaleString()} ₽
                            </Typography>
                        </Grid>

                        {estimate.guaranteed > 0 && estimate.total < estimate.guaranteed && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="warning.main">
                                        Будет выплачена гарантированная сумма: {estimate.guaranteed.toLocaleString()} ₽
                                    </Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        * Примерные значения: общая выручка 100,000₽, сувениры 20,000₽, кафе 15,000₽
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<SaveIcon />}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SalaryConfigDialog;
